import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const inr = (n) => `₹${new Intl.NumberFormat("en-IN").format(Math.round(n || 0))}`;
const firstOfMonth = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };
const today = () => new Date().toISOString().slice(0, 10);

export default function AdminReports() {
  const [range, setRange] = useState({ date_from: firstOfMonth(), date_to: today() });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/reports", { params: range }).then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [range.date_from, range.date_to]);

  const CARDS = [
    { k: "enquiries", l: "Enquiries" }, { k: "buyers", l: "New Buyers" }, { k: "requirements", l: "Requirements" },
    { k: "leads", l: "Leads" }, { k: "deals", l: "Deals" }, { k: "deals_completed", l: "Completed" },
    { k: "deals_lost", l: "Lost / Cancelled" }, { k: "conversion_rate", l: "Conversion %", pct: true },
  ];

  return (
    <div>
      <h1 className="font-display text-2xl font-extrabold text-slate-900">Reports</h1>
      <p className="mt-1 text-slate-500">Performance and conversion for a date range.</p>

      <div className="mt-4 flex flex-wrap items-end gap-3 rounded-xl border border-slate-200 bg-white p-4">
        <div><Label>From</Label><Input data-testid="report-from" type="date" value={range.date_from} onChange={(e) => setRange({ ...range, date_from: e.target.value })} /></div>
        <div><Label>To</Label><Input data-testid="report-to" type="date" value={range.date_to} onChange={(e) => setRange({ ...range, date_to: e.target.value })} /></div>
        <button data-testid="report-all-time" onClick={() => setRange({ date_from: "", date_to: "" })} className="rounded-full border border-slate-300 px-4 py-2 text-sm font-bold text-slate-700">All time</button>
      </div>

      {loading || !data ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div> : (
        <>
          <div className="mt-5 grid grid-cols-2 gap-4 lg:grid-cols-4">
            {CARDS.map((c) => (
              <div key={c.k} data-testid={`report-${c.k}`} className="rounded-xl border border-slate-200 bg-white p-5">
                <p className="font-display text-3xl font-extrabold text-slate-900">{c.pct ? `${data[c.k]}%` : data[c.k]}</p>
                <p className="mt-0.5 text-xs font-semibold uppercase tracking-wider text-slate-400">{c.l}</p>
              </div>
            ))}
          </div>

          <div className="mt-4 grid grid-cols-2 gap-4 sm:max-w-lg">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-emerald-600">Revenue Paid</p>
              <p className="font-display text-2xl font-extrabold text-emerald-700">{inr(data.revenue_paid)}</p>
            </div>
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-amber-600">Revenue Pending</p>
              <p className="font-display text-2xl font-extrabold text-amber-700">{inr(data.revenue_pending)}</p>
            </div>
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-3">
            <Breakdown title="Enquiries by Source" obj={data.by_source} />
            <Breakdown title="Leads by Status" obj={data.lead_status} />
            <Breakdown title="Requirements by Status" obj={data.requirement_status} />
          </div>
        </>
      )}
    </div>
  );
}

function Breakdown({ title, obj }) {
  const entries = Object.entries(obj || {}).sort((a, b) => b[1] - a[1]);
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <h3 className="font-display font-bold text-slate-900">{title}</h3>
      <div className="mt-2 space-y-1.5">
        {entries.length === 0 && <p className="text-sm text-slate-400">No data.</p>}
        {entries.map(([k, v]) => (
          <div key={k} className="flex items-center justify-between text-sm">
            <span className="text-slate-600">{k}</span>
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-bold text-slate-700">{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
